import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { classesApi, teachersApi } from '../../services/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, School, UserPlus, Loader2 } from 'lucide-react';

const ClassesPage = () => {
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [assigningClass, setAssigningClass] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    section: '',
    academic_year: '2024-2025',
    whatsapp_group_url: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [classesRes, teachersRes] = await Promise.all([
        classesApi.getAll(),
        teachersApi.getAll({ limit: 100 })
      ]);
      setClasses(classesRes.data.classes || []);
      setTeachers(teachersRes.data.teachers || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      if (editingClass) {
        await classesApi.update(editingClass.id, formData);
        toast.success('Class updated successfully');
      } else {
        await classesApi.create(formData);
        toast.success('Class created successfully');
      }
      setIsModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setFormLoading(false);
    }
  };

  const handleAssignTeacher = async () => {
    if (!selectedTeacher || !assigningClass) return;
    setFormLoading(true);

    try {
      await classesApi.assignTeacher(assigningClass.id, selectedTeacher);
      toast.success('Teacher assigned successfully');
      setIsAssignModalOpen(false);
      setSelectedTeacher('');
      setAssigningClass(null);
      loadData();
    } catch (error) {
      toast.error('Failed to assign teacher');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (cls) => {
    setEditingClass(cls);
    setFormData({
      name: cls.name || '',
      section: cls.section || '',
      academic_year: cls.academic_year || '2024-2025',
      whatsapp_group_url: cls.whatsapp_group_url || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;
    
    try {
      await classesApi.delete(id);
      toast.success('Class deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete class');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      section: '',
      academic_year: '2024-2025',
      whatsapp_group_url: ''
    });
    setEditingClass(null);
  };

  const getTeacherName = (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId);
    return teacher ? teacher.name : '-';
  };

  return (
    <div className="space-y-6" data-testid="classes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Classes</h1>
          <p className="text-muted-foreground mt-1">Manage class records and teacher assignments</p>
        </div>
        <Dialog open={isModalOpen} onOpenChange={(open) => { setIsModalOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-class-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Class
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingClass ? 'Edit Class' : 'Add New Class'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Class Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Class 10"
                  required
                  data-testid="class-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="section">Section</Label>
                <Input
                  id="section"
                  value={formData.section}
                  onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                  placeholder="e.g., A, B, C"
                  data-testid="class-section-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="academic_year">Academic Year</Label>
                <Input
                  id="academic_year"
                  value={formData.academic_year}
                  onChange={(e) => setFormData({ ...formData, academic_year: e.target.value })}
                  placeholder="e.g., 2024-2025"
                  data-testid="class-year-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="whatsapp_group_url">WhatsApp Group Link (optional)</Label>
                <Input
                  id="whatsapp_group_url"
                  value={formData.whatsapp_group_url}
                  onChange={(e) => setFormData({ ...formData, whatsapp_group_url: e.target.value })}
                  placeholder="https://chat.whatsapp.com/XXXXX"
                  data-testid="class-whatsapp-url-input"
                />
                <p className="text-xs text-muted-foreground">
                  Used when a teacher publishes homework for this class.
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={formLoading} data-testid="class-submit-button">
                  {formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editingClass ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Assign Teacher Modal */}
      <Dialog open={isAssignModalOpen} onOpenChange={setIsAssignModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Teacher to {assigningClass?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Teacher</Label>
              <Select value={selectedTeacher} onValueChange={setSelectedTeacher}>
                <SelectTrigger data-testid="assign-teacher-select">
                  <SelectValue placeholder="Select a teacher" />
                </SelectTrigger>
                <SelectContent>
                  {teachers.map((teacher) => (
                    <SelectItem key={teacher.id} value={teacher.id}>
                      {teacher.name} - {teacher.subject_specialization || 'General'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsAssignModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAssignTeacher} 
                disabled={!selectedTeacher || formLoading}
                data-testid="assign-teacher-button"
              >
                {formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Assign
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Table */}
      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <School className="h-5 w-5 text-primary" />
            Class List ({classes.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner h-8 w-8 text-primary" />
            </div>
          ) : classes.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Class Name</TableHead>
                    <TableHead>Section</TableHead>
                    <TableHead>Academic Year</TableHead>
                    <TableHead>Assigned Teacher</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {classes.map((cls) => (
                    <TableRow key={cls.id} className="table-row-hover">
                      <TableCell className="font-medium">{cls.name}</TableCell>
                      <TableCell>{cls.section || '-'}</TableCell>
                      <TableCell>{cls.academic_year || '-'}</TableCell>
                      <TableCell>{getTeacherName(cls.teacher_id)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setAssigningClass(cls);
                            setSelectedTeacher(cls.teacher_id || '');
                            setIsAssignModalOpen(true);
                          }}
                          data-testid={`assign-teacher-${cls.id}`}
                        >
                          <UserPlus className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(cls)}
                          data-testid={`edit-class-${cls.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(cls.id)}
                          data-testid={`delete-class-${cls.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <School className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No classes found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClassesPage;
